<script setup lang="ts">
import { computed } from 'vue'
import { useAppMode } from '@/composables/useAppMode'

const props = defineProps<{
  value: string | null
  lost?: boolean
}>()

const rank = computed(() =>
  props.value ? (props.value[0].toUpperCase() === 'T' ? '10' : props.value[0]) : null
)
const suit = computed(() => (props.value ? props.value[1].toLowerCase() : null))
const { mode } = useAppMode()
</script>
<template>
  <div
    class="card"
    :class="{
      'card--hidden': !value,
      'card--lost': lost,
      [`card--${mode}`]: true
    }"
  >
    <div
      class="card__side card__front"
      :class="{
        'card--value': Boolean(value),
        [`card--${suit}`]: suit
      }"
      :data-rank="rank"
    />
    <div class="card__side card__back" />
  </div>
</template>

<style lang="scss">
.card {
  --card-radius: calc(0.15 * var(--card-width));

  transition: all 0.5s ease-in-out;
  position: relative;
  border-radius: var(--card-radius);
  box-shadow: 0px 2.2666666507720947px 5.666666507720947px 0px #39174140;
  perspective: 1000px;
  overflow: hidden;
  width: var(--card-width) !important;
  height: var(--card-height) !important;
  font-size: calc(0.3 * var(--card-height));
  flex-shrink: 0;

  &--lost {
    filter: brightness(0.5);
  }

  &--value {
    background-size: 60%, 30%;
    background-repeat: no-repeat, no-repeat;
    background-position:
      90% 93%,
      16% 42%;
  }

  &--c {
    background-image: url('@/assets/cards/club.png'), url('@/assets/cards/club.png');
    color: #21252d;
  }

  &--s {
    background-image: url('@/assets/cards/spade.png'), url('@/assets/cards/spade.png');
    color: #21252d;
  }

  &--h {
    background-image: url('@/assets/cards/heart.png'), url('@/assets/cards/heart.png');
    color: #cc3933;
  }

  &--d {
    background-image: url('@/assets/cards/diamond.png'), url('@/assets/cards/diamond.png');
    color: #cc3933;
  }

  &--hidden {
    border: none;

    .card__front {
      transform: rotateY(180deg);
    }

    .card__back {
      transform: rotateY(0deg);
    }
  }

  &__front {
    background-color: white;
    transform: rotateY(0deg);

    &::after {
      position: absolute;
      display: flex;
      height: 44%;
      width: 52%;
      text-align: center;
      justify-content: center;
      align-items: center;
      content: attr(data-rank);
      font-weight: 500;
      margin-top: -4%;
    }
  }

  &__side {
    position: absolute;
    border-radius: 6px;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    transition:
      width 0.5s ease-in-out,
      height 0.5s ease-in-out,
      transform 0.5s ease-in-out;
  }

  &__back {
    width: 100%;
    height: 100%;
    background-image: url('@/assets/cards/back.png');
    background-size: cover;
    border-radius: 6px;
    backface-visibility: hidden;
    transform: rotateY(-180deg);
  }

  &__lost {
    border-radius: 6px;
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(28, 30, 38, 0.65);
    z-index: 2;

    &-enter-active {
      transition: all 0.3s ease-in-out;
    }

    &-enter-from {
      opacity: 0;
    }

    &-enter-to {
      opacity: 1;
    }
  }
}
</style>
